import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Row, Col, Card, Form, Button, Table, Badge, Modal, 
  Spinner, InputGroup, Alert, OverlayTrigger, Tooltip 
} from 'react-bootstrap';
import { 
  FaSearch, FaPlus, FaEdit, FaSave, FaTimes, FaTrash, 
  FaArrowLeft, FaExclamationTriangle, FaCheckCircle, FaTag,
  FaInfo, FaSortUp, FaSortDown, FaSort
} from 'react-icons/fa';
import { makeAuthenticatedRequest } from '../../services/api';

const KnowledgeExplorer = ({ setError, setSuccess, selectedAgent }) => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedLearningId, setSelectedLearningId] = useState(null);
  
  const [dropdownFields, setDropdownFields] = useState(null);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  
  const [learnings, setLearnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_count: 0,
    total_pages: 0
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    learning_type: '',
    status: '',
    source_type: '',
    min_confidence: '',
    max_confidence: ''
  });
  const searchDebounceRef = useRef(null);
  
  // Selection state
  const [selectedLearnings, setSelectedLearnings] = useState(new Set());
  const selectAllRef = useRef(null);
  
  const [learningDetail, setLearningDetail] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  
  const [addValues, setAddValues] = useState({
    agent_id: '',
    knowledge_text: '',
    topic_hint: '',
    summary: '',
    learning_type: '',
    learning_classification: '',
    primary_category: '',
    source_id: '',
    source_type: '',
    status: 'active',
    scope: 'agent',
    confidence: 0.5,
    importance_score: 0.5,
    trust_score: 0.5,
    credibility_score: '',
    key_topics: [],
    entities: [],
    search_terms: [],
    micro_agent_id: '',
    requires_review: false,
    archived: false
  });
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Delete selected states
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
  
  useEffect(() => {
    fetchDropdownFields();
  }, []);
  
  useEffect(() => {
    if (currentView === 'list') {
      fetchLearnings();
    }
  }, [currentView, pagination.page, sortBy, sortOrder]);
  
  useEffect(() => {
    if (currentView === 'list') {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchLearnings();
      }, 300);
    }
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchText, filters]);
  
  // Update indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      const allSelected = learnings.length > 0 && selectedLearnings.size === learnings.length;
      const someSelected = selectedLearnings.size > 0 && selectedLearnings.size < learnings.length;
      selectAllRef.current.indeterminate = someSelected;
      selectAllRef.current.checked = allSelected;
    }
  }, [selectedLearnings, learnings]);
  
  const fetchDropdownFields = async () => {
    setDropdownsLoading(true);
    try {
      const response = await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', { 
        operation: 'get_dropdown_fields' 
      });
      
      let data = response;
      if (response && typeof response.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse dropdown fields response:', e);
        }
      } else if (response && typeof response.body === 'object' && response.body !== null) {
        data = response.body;
      }
      
      setDropdownFields(data);
    } catch (error) {
      console.error('Failed to fetch dropdown fields:', error);
      setError?.('Failed to load form options');
    } finally {
      setDropdownsLoading(false);
    }
  };
  
  const fetchLearnings = async () => {
    setLoading(true);
    try {
      const hasActiveFilters = searchText.trim() || filters.learning_type || filters.status || 
                               filters.source_type || filters.min_confidence || filters.max_confidence;
      
      let payload;
      if (hasActiveFilters) {
        payload = {
          operation: 'search_learnings',
          page: pagination.page,
          page_size: pagination.page_size
        };
        if (searchText.trim()) payload.search_text = searchText.trim();
        if (filters.learning_type) payload.learning_type = filters.learning_type;
        if (filters.status) payload.status = filters.status;
        if (filters.source_type) payload.source_type = filters.source_type;
        if (filters.min_confidence) payload.min_confidence = parseFloat(filters.min_confidence);
        if (filters.max_confidence) payload.max_confidence = parseFloat(filters.max_confidence);
      } else {
        payload = {
          operation: 'list_learnings',
          page: pagination.page,
          page_size: pagination.page_size,
          sort_by: sortBy,
          sort_order: sortOrder
        };
      }
      
      const response = await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', payload);
      
      let data = response;
      if (response && typeof response.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse learnings response:', e);
        }
      } else if (response && typeof response.body === 'object' && response.body !== null) {
        data = response.body;
      }
      
      const learningsData = data.learnings || [];
      const paginationData = data.pagination || {};
      
      // Apply client-side sort for non-server columns if needed
      const clientSortColumns = ['micro_agent_id', 'learning_classification', 'source_identifier', 'last_accessed_date'];
      if (clientSortColumns.includes(sortBy)) {
        clientSideSort(learningsData, sortBy, sortOrder);
      } else {
        setLearnings(learningsData);
      }
      
      setPagination(prev => ({
        ...prev,
        total_count: paginationData.total_count || 0,
        total_pages: paginationData.total_pages || 0
      }));
    } catch (error) {
      console.error('Failed to fetch learnings:', error);
      setError?.(`Failed to fetch learnings: ${error.message}`);
      setLearnings([]);
    } finally {
      setLoading(false);
    }
  };
  
  const clientSideSort = (data, column, order) => {
    const sorted = [...data].sort((a, b) => {
      let aVal = a[column];
      let bVal = b[column];
      
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'ASC' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      return order === 'ASC' ? aVal - bVal : bVal - aVal;
    });
    setLearnings(sorted);
  };
  
  const handleSort = (column) => {
    const serverSortable = ['created_at', 'updated_at', 'importance_score', 'confidence', 'trust_score', 'credibility_score', 'learning_type', 'status'];
    
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };
  
  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <FaSort className="ms-1 text-muted" style={{ fontSize: '0.7rem', opacity: 0.3 }} />;
    }
    return sortOrder === 'ASC' 
      ? <FaSortUp className="ms-1 text-info" style={{ fontSize: '0.7rem' }} />
      : <FaSortDown className="ms-1 text-info" style={{ fontSize: '0.7rem' }} />;
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(learnings.map(l => l.learning_id));
      setSelectedLearnings(newSelected);
    } else {
      setSelectedLearnings(new Set());
    }
  };
  
  const handleSelectLearning = (learningId, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedLearnings);
    if (newSelected.has(learningId)) {
      newSelected.delete(learningId);
    } else {
      newSelected.add(learningId);
    }
    setSelectedLearnings(newSelected);
  };
  
  const handleDeleteSelected = async () => {
    setDeleteSelectedLoading(true);
    const idsToDelete = Array.from(selectedLearnings);
    let successCount = 0;
    let errorCount = 0;
    
    for (const learningId of idsToDelete) {
      try {
        await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', {
          operation: 'delete_learning',
          learning_id: learningId
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to delete learning ${learningId}:`, error);
        errorCount++;
      }
    }
    
    setDeleteSelectedLoading(false);
    setShowDeleteSelectedModal(false);
    setSelectedLearnings(new Set());
    
    if (successCount > 0) {
      setSuccess?.(`Successfully deleted ${successCount} learning(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
    }
    if (errorCount > 0 && successCount === 0) {
      setError?.(`Failed to delete ${errorCount} learning(s)`);
    }
    
    fetchLearnings();
  };
  
  const handleRowClick = async (learningId) => {
    setSelectedLearningId(learningId);
    setCurrentView('edit');
    setDetailLoading(true);
    await fetchLearningDetail(learningId);
  };
  
  const fetchLearningDetail = async (learningId) => {
    setDetailLoading(true);
    try {
      const response = await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', {
        operation: 'get_learning',
        learning_id: learningId
      });
      
      let data = response;
      if (response && typeof response.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse learning detail response:', e);
        }
      } else if (response && typeof response.body === 'object' && response.body !== null) {
        data = response.body;
      }
      
      const learning = data.learning || data;
      
      setLearningDetail(learning);
      setEditValues(learning);
      setOriginalValues(JSON.parse(JSON.stringify(learning)));
    } catch (error) {
      console.error('Failed to fetch learning detail:', error);
      setError?.(`Failed to load learning details: ${error.message}`);
    } finally {
      setDetailLoading(false);
    }
  };
  
  const getDirtyFields = () => {
    const dirty = {};
    Object.keys(editValues).forEach(key => {
      if (JSON.stringify(editValues[key]) !== JSON.stringify(originalValues[key])) {
        dirty[key] = editValues[key];
      }
    });
    return dirty;
  };
  
  const isDirty = Object.keys(getDirtyFields()).length > 0;
  
  const handleSave = async () => {
    const dirtyFields = getDirtyFields();
    if (Object.keys(dirtyFields).length === 0) return;
    
    setSaveLoading(true);
    try {
      await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', {
        operation: 'edit_learning',
        learning_id: selectedLearningId,
        updates: dirtyFields
      });
      setSuccess?.('Learning updated successfully');
      setOriginalValues(JSON.parse(JSON.stringify(editValues)));
      await fetchLearningDetail(selectedLearningId);
    } catch (error) {
      console.error('Failed to save learning:', error);
      setError?.(`Failed to save: ${error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };
  
  const handleCancel = () => {
    setEditValues(JSON.parse(JSON.stringify(originalValues)));
  };
  
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', {
        operation: 'delete_learning',
        learning_id: selectedLearningId
      });
      setSuccess?.('Learning deleted successfully');
      setShowDeleteModal(false);
      setCurrentView('list');
      fetchLearnings();
    } catch (error) {
      console.error('Failed to delete learning:', error);
      setError?.(`Failed to delete: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const validateAddForm = () => {
    const errors = {};
    
    if (!addValues.agent_id || parseInt(addValues.agent_id) <= 0) {
      errors.agent_id = 'Agent ID must be a positive integer';
    }
    
    if (!addValues.knowledge_text || addValues.knowledge_text.trim().length === 0) {
      errors.knowledge_text = 'Knowledge text is required';
    }
    
    if (addValues.confidence !== '' && (addValues.confidence < 0 || addValues.confidence > 1)) {
      errors.confidence = 'Confidence must be between 0.0 and 1.0';
    }
    
    if (addValues.importance_score !== '' && (addValues.importance_score < 0 || addValues.importance_score > 1)) {
      errors.importance_score = 'Importance score must be between 0.0 and 1.0';
    }
    
    if (addValues.trust_score !== '' && (addValues.trust_score < 0 || addValues.trust_score > 1)) {
      errors.trust_score = 'Trust score must be between 0.0 and 1.0';
    }
    
    if (addValues.credibility_score !== '' && (addValues.credibility_score < 0 || addValues.credibility_score > 1)) {
      errors.credibility_score = 'Credibility score must be between 0.0 and 1.0';
    }
    
    return errors;
  };
  
  const handleAdd = async (e) => {
    e.preventDefault();
    
    const errors = validateAddForm();
    setAddErrors(errors);
    
    if (Object.keys(errors).length > 0) return;
    
    setAddLoading(true);
    try {
      const payload = {
        operation: 'add_learning',
        agent_id: parseInt(addValues.agent_id),
        knowledge_text: addValues.knowledge_text.trim()
      };
      
      Object.keys(addValues).forEach(key => {
        if (key !== 'agent_id' && key !== 'knowledge_text' && addValues[key] !== '' && addValues[key] !== null) {
          if (Array.isArray(addValues[key])) {
            if (addValues[key].length > 0) {
              payload[key] = addValues[key];
            }
          } else {
            payload[key] = addValues[key];
          }
        }
      });
      
      const response = await makeAuthenticatedRequest('POST', '/knowledge/knowledge_write', payload);
      
      let data = response;
      if (response && typeof response.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse add learning response:', e);
        }
      } else if (response && typeof response.body === 'object' && response.body !== null) {
        data = response.body;
      }
      
      setSuccess?.('Learning added successfully');
      
      if (data.learning_id) {
        setSelectedLearningId(data.learning_id);
        setCurrentView('edit');
        await fetchLearningDetail(data.learning_id);
      } else {
        setCurrentView('list');
        fetchLearnings();
      }
    } catch (error) {
      console.error('Failed to add learning:', error);
      setError?.(`Failed to add: ${error.message}`);
    } finally {
      setAddLoading(false);
    }
  };
  
  const handleAddTag = (field, value) => {
    if (!value || !value.trim()) return;
    const currentValues = editValues[field] || [];
    if (!currentValues.includes(value.trim())) {
      setEditValues(prev => ({
        ...prev,
        [field]: [...currentValues, value.trim()]
      }));
    }
  };
  
  const handleRemoveTag = (field, index) => {
    setEditValues(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };
  
  const handleAddTagForAdd = (field, value) => {
    if (!value || !value.trim()) return;
    const currentValues = addValues[field] || [];
    if (!currentValues.includes(value.trim())) {
      setAddValues(prev => ({
        ...prev,
        [field]: [...currentValues, value.trim()]
      }));
    }
  };
  
  const handleRemoveTagForAdd = (field, index) => {
    setAddValues(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };
  
  const handleSourceChange = (e) => {
    const selectedSource = dropdownFields?.knowledge_sources?.find(
      s => s.source_identifier === e.target.value
    );
    if (selectedSource) {
      setEditValues(prev => ({
        ...prev,
        source_id: selectedSource.source_identifier,
        source_type: selectedSource.source_type
      }));
    }
  };
  
  const handleSourceChangeForAdd = (e) => {
    const selectedSource = dropdownFields?.knowledge_sources?.find(
      s => s.source_identifier === e.target.value
    );
    if (selectedSource) {
      setAddValues(prev => ({
        ...prev,
        source_id: selectedSource.source_identifier,
        source_type: selectedSource.source_type
      }));
    }
  };
  
  const renderListView = () => (
    <div>
      <Card className="kb-card mb-3">
        <Card.Body className="p-3">
          <Row className="g-2 mb-3">
            <Col md={6}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-transparent border-end-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search learnings..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-end">
              <Button variant="success" size="sm" onClick={() => setCurrentView('add')}>
                <FaPlus className="me-2" /> Add Learning
              </Button>
            </Col>
          </Row>
          
          <Row className="g-2">
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.learning_type}
                onChange={(e) => setFilters(prev => ({ ...prev, learning_type: e.target.value }))}
                className="bg-dark text-white border-secondary"
              >
                <option value="">All Types</option>
                {dropdownFields?.learning_types?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="bg-dark text-white border-secondary"
              >
                <option value="">All Statuses</option>
                {dropdownFields?.statuses?.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.source_type}
                onChange={(e) => setFilters(prev => ({ ...prev, source_type: e.target.value }))}
                className="bg-dark text-white border-secondary"
              >
                <option value="">All Sources</option>
                {dropdownFields?.source_types?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-dark text-muted border-secondary">Conf</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  placeholder="Min"
                  value={filters.min_confidence}
                  onChange={(e) => setFilters(prev => ({ ...prev, min_confidence: e.target.value }))}
                  className="bg-dark text-white border-secondary"
                />
                <Form.Control
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  placeholder="Max"
                  value={filters.max_confidence}
                  onChange={(e) => setFilters(prev => ({ ...prev, max_confidence: e.target.value }))}
                  className="bg-dark text-white border-secondary"
                />
              </InputGroup>
            </Col>
            <Col md={3} className="text-end">
              <span className="text-muted small">Total: {pagination.total_count}</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card className="kb-card">
        <div className="table-responsive" style={{ overflowX: 'auto' }}>
          <Table className="kb-table mb-0" hover>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    ref={selectAllRef}
                    type="checkbox"
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('topic_hint')}
                >
                  Topic Hint {renderSortIcon('topic_hint')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('learning_classification')}
                >
                  Classification {renderSortIcon('learning_classification')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('source_identifier')}
                >
                  Source {renderSortIcon('source_identifier')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('micro_agent_id')}
                >
                  Agent ID {renderSortIcon('micro_agent_id')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('confidence')}
                >
                  Confidence {renderSortIcon('confidence')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('credibility_score')}
                >
                  Credibility {renderSortIcon('credibility_score')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('importance_score')}
                >
                  Importance {renderSortIcon('importance_score')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('trust_score')}
                >
                  Trust {renderSortIcon('trust_score')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('last_accessed_date')}
                >
                  Last Accessed {renderSortIcon('last_accessed_date')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('created_at')}
                >
                  Created At {renderSortIcon('created_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center p-4">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : learnings.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center p-4 text-muted">
                    No learnings found
                  </td>
                </tr>
              ) : (
                learnings.map((learning) => (
                  <tr 
                    key={learning.learning_id} 
                    onClick={() => handleRowClick(learning.learning_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedLearnings.has(learning.learning_id)}
                        onChange={(e) => handleSelectLearning(learning.learning_id, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: 'none' }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{learning.knowledge_text_preview || learning.knowledge_text?.substring(0, 100) || '—'}</Tooltip>
                        }
                      >
                        <span>{learning.topic_hint || '—'}</span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      <span className="small">{learning.learning_classification || '—'}</span>
                    </td>
                    <td>
                      <span className="small text-muted" style={{ maxWidth: '150px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {learning.source_identifier || '—'}
                      </span>
                    </td>
                    <td>
                      <span className="small">{learning.micro_agent_id || '—'}</span>
                    </td>
                    <td>
                      <span className={
                        (typeof learning.confidence === 'number' ? learning.confidence : (parseFloat(learning.confidence) || 0)) > 0.7 
                          ? 'text-success' 
                          : 'text-warning'
                      }>
                        {learning.confidence != null 
                          ? (((typeof learning.confidence === 'number' ? learning.confidence : (parseFloat(learning.confidence) || 0)) * 100).toFixed(0) + '%')
                          : '—'
                        }
                      </span>
                    </td>
                    <td>
                      <span className="small">
                        {learning.credibility_score != null 
                          ? (typeof learning.credibility_score === 'number' ? learning.credibility_score : (parseFloat(learning.credibility_score) || 0)).toFixed(2)
                          : '—'
                        }
                      </span>
                    </td>
                    <td>
                      <span className="small">
                        {learning.importance_score != null 
                          ? (typeof learning.importance_score === 'number' ? learning.importance_score : (parseFloat(learning.importance_score) || 0)).toFixed(2)
                          : '—'
                        }
                      </span>
                    </td>
                    <td>
                      <span className="small">
                        {learning.trust_score != null 
                          ? (typeof learning.trust_score === 'number' ? learning.trust_score : (parseFloat(learning.trust_score) || 0)).toFixed(2)
                          : '—'
                        }
                      </span>
                    </td>
                    <td>
                      <span className="small text-muted">
                        {learning.last_accessed_date ? new Date(learning.last_accessed_date).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td>
                      <Badge bg={learning.status === 'active' ? 'success' : 'secondary'} className="kb-badge">
                        {learning.status || '—'}
                      </Badge>
                    </td>
                    <td>{learning.created_at ? new Date(learning.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        
        {/* Footer with counter and delete button */}
        <div className="d-flex justify-content-between align-items-center p-3 border-top border-secondary">
          <div>
            <span className="text-muted small">
              Showing {learnings.length} of {pagination.total_count} total learnings
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {selectedLearnings.size > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteSelectedModal(true)}
                disabled={deleteSelectedLoading}
              >
                {deleteSelectedLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaTrash className="me-2" /> Delete Selected ({selectedLearnings.size})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        
        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-center align-items-center p-3 border-top border-secondary">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <span className="mx-3 text-muted">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page === pagination.total_pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
  
  const renderDetailView = () => {
    if (detailLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3 text-muted">Loading learning details...</p>
        </div>
      );
    }
    
    if (!learningDetail) {
      return (
        <div className="text-center p-5 text-muted">
          Learning not found
        </div>
      );
    }
    
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="outline-secondary" size="sm" onClick={() => setCurrentView('list')}>
            <FaArrowLeft className="me-2" /> Back to List
          </Button>
          <div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="me-2"
              onClick={handleCancel}
              disabled={!isDirty}
            >
              <FaTimes className="me-2" /> Cancel
            </Button>
            <Button 
              variant="primary" 
              size="sm" 
              className="me-2"
              onClick={handleSave}
              disabled={!isDirty || saveLoading}
            >
              {saveLoading ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save</>}
            </Button>
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => setShowDeleteModal(true)}
            >
              <FaTrash className="me-2" /> Delete
            </Button>
          </div>
        </div>
        
        <Card className="kb-card mb-3">
          <Card.Body className="p-3">
            <Row className="small">
              <Col md={4}>
                <strong>Learning ID:</strong> <span className="font-monospace">{learningDetail.learning_id}</span>
              </Col>
              <Col md={4}>
                <strong>Created:</strong> {learningDetail.created_at ? new Date(learningDetail.created_at).toLocaleString() : '—'}
              </Col>
              <Col md={4}>
                <strong>Updated:</strong> {learningDetail.updated_at ? new Date(learningDetail.updated_at).toLocaleString() : '—'}
              </Col>
            </Row>
            <Row className="small mt-2">
              <Col md={4}>
                <strong>Last Accessed:</strong> {learningDetail.last_accessed_date ? new Date(learningDetail.last_accessed_date).toLocaleString() : 'Never'}
              </Col>
              <Col md={4}>
                <strong>Access Count:</strong> {Number(learningDetail.access_count || 0)}
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="kb-card">
          <Card.Body style={{ padding: '24px' }}>
            <Form>
              <Row>
                <Col md={12}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Knowledge Text <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={16}
                      value={editValues.knowledge_text || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, knowledge_text: e.target.value }))}
                      className="kb-large-textarea"
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        padding: '16px',
                        fontSize: '0.95rem',
                        lineHeight: '1.6',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(0, 229, 255, 0.15)',
                        color: 'var(--pure-white)',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Topic Hint
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editValues.topic_hint || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, topic_hint: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Summary
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editValues.summary || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, summary: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Learning Type
                    </Form.Label>
                    <Form.Select
                      value={editValues.learning_type || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, learning_type: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.learning_types?.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Learning Classification
                    </Form.Label>
                    <Form.Select
                      value={editValues.learning_classification || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, learning_classification: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.learning_classifications?.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Primary Category
                    </Form.Label>
                    <Form.Select
                      value={editValues.primary_category || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, primary_category: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.primary_categories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Source
                    </Form.Label>
                    <Form.Select
                      value={editValues.source_id || ''}
                      onChange={handleSourceChange}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.knowledge_sources?.map(source => (
                        <option key={source.source_identifier} value={source.source_identifier}>
                          {source.display_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Status
                    </Form.Label>
                    <Form.Select
                      value={editValues.status || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, status: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.statuses?.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Scope
                    </Form.Label>
                    <Form.Select
                      value={editValues.scope || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, scope: e.target.value }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    >
                      <option value="">Select...</option>
                      {dropdownFields?.scopes?.map(scope => (
                        <option key={scope} value={scope}>{scope}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={3}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Confidence (0.0-1.0)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editValues.confidence != null ? editValues.confidence : ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, confidence: e.target.value !== '' ? parseFloat(e.target.value) : null }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Importance Score (0.0-1.0)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editValues.importance_score != null ? editValues.importance_score : ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, importance_score: e.target.value !== '' ? parseFloat(e.target.value) : null }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Trust Score (0.0-1.0)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editValues.trust_score != null ? editValues.trust_score : ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, trust_score: e.target.value !== '' ? parseFloat(e.target.value) : null }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Credibility Score (0.0-1.0)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editValues.credibility_score != null ? editValues.credibility_score : ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, credibility_score: e.target.value !== '' ? parseFloat(e.target.value) : null }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <TagInput
                    label="Key Topics"
                    tags={editValues.key_topics || []}
                    onAddTag={(value) => handleAddTag('key_topics', value)}
                    onRemoveTag={(index) => handleRemoveTag('key_topics', index)}
                  />
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <TagInput
                    label="Entities"
                    tags={editValues.entities || []}
                    onAddTag={(value) => handleAddTag('entities', value)}
                    onRemoveTag={(index) => handleRemoveTag('entities', index)}
                  />
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <TagInput
                    label="Search Terms"
                    tags={editValues.search_terms || []}
                    onAddTag={(value) => handleAddTag('search_terms', value)}
                    onRemoveTag={(index) => handleRemoveTag('search_terms', index)}
                  />
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Agent ID
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={editValues.agent_id || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, agent_id: parseInt(e.target.value) || '' }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                      Micro Agent ID
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={editValues.micro_agent_id || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, micro_agent_id: parseInt(e.target.value) || '' }))}
                      className="bg-dark text-light border-secondary"
                      style={{ width: '100%', borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    label="Requires Review"
                    checked={editValues.requires_review || false}
                    onChange={(e) => setEditValues(prev => ({ ...prev, requires_review: e.target.checked }))}
                    className="text-light"
                    style={{ marginBottom: '20px' }}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    label="Archived"
                    checked={editValues.archived || false}
                    onChange={(e) => setEditValues(prev => ({ ...prev, archived: e.target.checked }))}
                    className="text-light"
                    style={{ marginBottom: '20px' }}
                  />
                </Col>
              </Row>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={!isDirty}
                  style={{ padding: '8px 20px' }}
                >
                  <FaTimes className="me-2" /> Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={!isDirty || saveLoading}
                  style={{ padding: '8px 20px' }}
                >
                  {saveLoading ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save Changes</>}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );
  };
  
  const renderAddView = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button variant="outline-secondary" size="sm" onClick={() => setCurrentView('list')}>
          <FaArrowLeft className="me-2" /> Back to List
        </Button>
      </div>
      
      <Card className="kb-card">
        <Card.Header className="kb-card-header">
          <h5 className="mb-0"><FaPlus className="me-2" />Add New Learning</h5>
        </Card.Header>
        <Card.Body style={{ padding: '24px' }}>
          <Form onSubmit={handleAdd}>
            <Row>
              <Col md={12}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Agent ID <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={addValues.agent_id}
                    onChange={(e) => setAddValues(prev => ({ ...prev, agent_id: e.target.value }))}
                    isInvalid={!!addErrors.agent_id}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.agent_id}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Knowledge Text <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={16}
                    value={addValues.knowledge_text}
                    onChange={(e) => setAddValues(prev => ({ ...prev, knowledge_text: e.target.value }))}
                    isInvalid={!!addErrors.knowledge_text}
                    className="kb-large-textarea"
                    style={{
                      width: '100%',
                      minHeight: '400px',
                      padding: '16px',
                      fontSize: '0.95rem',
                      lineHeight: '1.6',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(0, 229, 255, 0.15)',
                      color: 'var(--pure-white)',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.knowledge_text}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Topic Hint
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={addValues.topic_hint}
                    onChange={(e) => setAddValues(prev => ({ ...prev, topic_hint: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Summary
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={addValues.summary}
                    onChange={(e) => setAddValues(prev => ({ ...prev, summary: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Learning Type
                  </Form.Label>
                  <Form.Select
                    value={addValues.learning_type}
                    onChange={(e) => setAddValues(prev => ({ ...prev, learning_type: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.learning_types?.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Learning Classification
                  </Form.Label>
                  <Form.Select
                    value={addValues.learning_classification}
                    onChange={(e) => setAddValues(prev => ({ ...prev, learning_classification: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.learning_classifications?.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Primary Category
                  </Form.Label>
                  <Form.Select
                    value={addValues.primary_category}
                    onChange={(e) => setAddValues(prev => ({ ...prev, primary_category: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.primary_categories?.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Source
                  </Form.Label>
                  <Form.Select
                    value={addValues.source_id}
                    onChange={handleSourceChangeForAdd}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.knowledge_sources?.map(source => (
                      <option key={source.source_identifier} value={source.source_identifier}>
                        {source.display_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Status
                  </Form.Label>
                  <Form.Select
                    value={addValues.status}
                    onChange={(e) => setAddValues(prev => ({ ...prev, status: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.statuses?.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Scope
                  </Form.Label>
                  <Form.Select
                    value={addValues.scope}
                    onChange={(e) => setAddValues(prev => ({ ...prev, scope: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  >
                    <option value="">Select...</option>
                    {dropdownFields?.scopes?.map(scope => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={3}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Confidence (0.0-1.0)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={addValues.confidence}
                    onChange={(e) => setAddValues(prev => ({ ...prev, confidence: parseFloat(e.target.value) }))}
                    isInvalid={!!addErrors.confidence}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.confidence}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Importance Score (0.0-1.0)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={addValues.importance_score}
                    onChange={(e) => setAddValues(prev => ({ ...prev, importance_score: parseFloat(e.target.value) }))}
                    isInvalid={!!addErrors.importance_score}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.importance_score}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Trust Score (0.0-1.0)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={addValues.trust_score}
                    onChange={(e) => setAddValues(prev => ({ ...prev, trust_score: parseFloat(e.target.value) }))}
                    isInvalid={!!addErrors.trust_score}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.trust_score}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Credibility Score (0.0-1.0)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={addValues.credibility_score}
                    onChange={(e) => setAddValues(prev => ({ ...prev, credibility_score: parseFloat(e.target.value) }))}
                    isInvalid={!!addErrors.credibility_score}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                  <Form.Control.Feedback type="invalid">{addErrors.credibility_score}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <TagInput
                  label="Key Topics"
                  tags={addValues.key_topics}
                  onAddTag={(value) => handleAddTagForAdd('key_topics', value)}
                  onRemoveTag={(index) => handleRemoveTagForAdd('key_topics', index)}
                />
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <TagInput
                  label="Entities"
                  tags={addValues.entities}
                  onAddTag={(value) => handleAddTagForAdd('entities', value)}
                  onRemoveTag={(index) => handleRemoveTagForAdd('entities', index)}
                />
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <TagInput
                  label="Search Terms"
                  tags={addValues.search_terms}
                  onAddTag={(value) => handleAddTagForAdd('search_terms', value)}
                  onRemoveTag={(index) => handleRemoveTagForAdd('search_terms', index)}
                />
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
                    Micro Agent ID
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={addValues.micro_agent_id}
                    onChange={(e) => setAddValues(prev => ({ ...prev, micro_agent_id: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                    style={{ width: '100%', borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Requires Review"
                  checked={addValues.requires_review}
                  onChange={(e) => setAddValues(prev => ({ ...prev, requires_review: e.target.checked }))}
                  className="text-light"
                  style={{ marginBottom: '20px' }}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Archived"
                  checked={addValues.archived}
                  onChange={(e) => setAddValues(prev => ({ ...prev, archived: e.target.checked }))}
                  className="text-light"
                  style={{ marginBottom: '20px' }}
                />
              </Col>
            </Row>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Button type="submit" variant="success" disabled={addLoading} style={{ padding: '8px 20px' }}>
                {addLoading ? <Spinner animation="border" size="sm" /> : <><FaPlus className="me-2" /> Add Learning</>}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
  
  return (
    <div className="knowledge-explorer">
      {currentView === 'list' && renderListView()}
      {currentView === 'edit' && renderDetailView()}
      {currentView === 'add' && renderAddView()}
      
      {/* Delete single modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><FaExclamationTriangle className="me-2" /> Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>Are you sure you want to permanently delete this learning?</p>
          <p className="text-muted small">Consider archiving instead by changing the status to 'archived'.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? <Spinner animation="border" size="sm" /> : 'Delete Permanently'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete selected modal */}
      <Modal show={showDeleteSelectedModal} onHide={() => setShowDeleteSelectedModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><FaExclamationTriangle className="me-2" /> Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>Are you sure you want to permanently delete <strong>{selectedLearnings.size}</strong> selected learning(s)?</p>
          <p className="text-muted small">This will delete each learning sequentially. Any failures will be reported.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteSelectedModal(false)} disabled={deleteSelectedLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSelected} disabled={deleteSelectedLoading}>
            {deleteSelectedLoading ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const TagInput = ({ label, tags, onAddTag, onRemoveTag }) => {
  const [inputValue, setInputValue] = useState('');
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        onAddTag(inputValue);
        setInputValue('');
      }
    }
  };
  
  return (
    <Form.Group style={{ marginBottom: '20px' }}>
      <Form.Label style={{ fontWeight: '600', color: 'var(--pure-white)', marginBottom: '8px', display: 'block' }}>
        {label}
      </Form.Label>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {tags.map((tag, index) => (
          <Badge key={index} bg="secondary" className="d-flex align-items-center gap-1 py-1 px-2">
            <FaTag size={10} />
            {tag}
            <FaTimes 
              size={12}
              style={{ cursor: 'pointer' }} 
              onClick={() => onRemoveTag(index)}
            />
          </Badge>
        ))}
      </div>
      <Form.Control
        type="text"
        placeholder={`Type and press Enter to add ${label.toLowerCase()}`}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="bg-dark text-light border-secondary"
        style={{ width: '100%', borderRadius: '6px' }}
      />
    </Form.Group>
  );
};

export default KnowledgeExplorer;